import type { BodyProps } from '@hover/blueprint';
import { Body, Box, Button, HStack, Icon, VStack } from '@hover/blueprint';
import { Card, CardBody } from '@hover/blueprint/chakra';
import { iExternalLink } from '@hover/icons';
import React from 'react';

import { FeaturedProductThumbnail } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductThumbnail';
import { useRenderingContext } from 'src/modules/renderings/hooks';
import { LinkExternal } from 'src/shared/components';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
} from 'src/shared/hooks';
import { linkPressed } from 'src/shared/telemetry';

export const DEFAULT_WIDTH = 410;
export const SMALL_WIDTH = 340;

const Label = (props: BodyProps) => (
  <Body
    color="neutral.600"
    fontWeight="300"
    marginY={0}
    maxWidth="50px"
    minWidth="50px"
    size={400}
    {...props}
  />
);

const Manufacturer = (props: BodyProps) => (
  <Body
    color="neutral.600"
    fontWeight="300"
    marginY={0}
    size={400}
    {...props}
  />
);

const Description = (props: BodyProps) => (
  <Body fontWeight="700" marginY={0} noOfLines={2} size={500} {...props} />
);

const LabeledValue = (props: BodyProps) => (
  <Body
    color="neutral.900"
    fontWeight="400"
    marginY={0}
    size={400}
    {...props}
  />
);

const ExternalLinkButton = () => (
  <Button
    _hover={{ transform: 'scale(1.1)' }}
    backgroundColor="neutral.100"
    data-group
    shadow="distance100"
    shape="circle"
    transitionDuration="350ms"
    transitionProperty="all"
    transitionTimingFunction="ease-out"
  >
    <Icon
      _groupHover={{ color: 'neutral.0' }}
      color="neutral.900"
      icon={iExternalLink}
      strokeWidth={3}
    />
  </Button>
);

export type FeaturedProduct = {
  id: string;
  category: string;
  description: string;
  manufacturer: string;
  manufacturerLink: string;
  productColor: string;
  productComposition: string;
  productSize: string;
  productTexture: string;
  thumbnailSrc: string;
  productColorHex: string;
  isPaintable: boolean;
};

type FeaturedProductCardProps = {
  cardSize?: 'small' | 'default';
  featuredProduct: FeaturedProduct;
};

const FeaturedProductCard = ({
  cardSize = 'default',
  featuredProduct,
}: FeaturedProductCardProps) => {
  const {
    category,
    description,
    manufacturer,
    manufacturerLink,
    productColor,
    productSize,
  } = featuredProduct;
  const t = useTranslate(TranslationNamespace.SHARED);
  const pageName = useCurrentPage();
  const { trackingData } = useRenderingContext();
  const imageSize = cardSize === 'small' ? '124px' : '136px';

  return (
    <Card
      backgroundColor="neutral.0"
      height="186px"
      maxWidth={cardSize === 'small' ? SMALL_WIDTH : DEFAULT_WIDTH}
      minWidth={cardSize === 'small' ? SMALL_WIDTH : DEFAULT_WIDTH}
      scrollSnapAlign={{ base: 'center', tablet: 'start' }}
      shadow="distance300"
    >
      <CardBody
        display="flex"
        overflow="hidden"
        paddingLeft={cardSize === 'small' ? 400 : 500}
        paddingRight={200}
        paddingY={300}
      >
        <Box position="relative">
          <FeaturedProductThumbnail
            featuredProduct={featuredProduct}
            imageSize={imageSize}
          />
          {!!category && (
            <Box
              backgroundColor="neutral.0"
              borderBottomEndRadius={300}
              borderTopEndRadius={300}
              paddingX={200}
              position="absolute"
              top={200}
            >
              <Body marginY={100} size={400}>
                {category}
              </Body>
            </Box>
          )}
        </Box>
        <VStack
          align="flex-start"
          marginLeft={cardSize === 'small' ? 400 : 500}
          spacing={100}
          width="100%"
        >
          <HStack
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
          >
            <VStack alignItems="flex-start" spacing={100}>
              {!!manufacturer && <Manufacturer>{manufacturer}</Manufacturer>}
              {!!description && <Description>{description}</Description>}
            </VStack>
            {!!manufacturerLink && (
              <Box>
                <LinkExternal
                  display="inline"
                  href={manufacturerLink}
                  onClick={() =>
                    linkPressed({
                      ...trackingData,
                      page_or_screen_name: pageName ?? '',
                      link_text: 'link-out-icon',
                      track_id: 'rendering-product-card-link-out',
                      primary_cta: false,
                    })
                  }
                >
                  <ExternalLinkButton />
                </LinkExternal>
              </Box>
            )}
          </HStack>
          {/* 
              We do not actually want the texture name here
              Instead we want to display material texture that will be a new variation facet
           */}
          {/* {!!productTexture && (
            <HStack alignItems="normal">
              <Label>{t('renderings.featuredProducts.card.texture')}</Label>
              <LabeledValue>{productTexture}</LabeledValue>
            </HStack>
          )} */}
          {!!productColor && (
            <HStack alignItems="normal">
              <Label>{t('renderings.featuredProducts.card.color')}</Label>
              <LabeledValue>{productColor}</LabeledValue>
            </HStack>
          )}
          {!!productSize && (
            <HStack alignItems="normal">
              <Label>{t('renderings.featuredProducts.card.size')}</Label>
              <LabeledValue>{productSize}</LabeledValue>
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export { FeaturedProductCard };
